import {get} from "aws-amplify/api";
import React, {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {RingLoader} from "react-spinners";
import {useParams, useNavigate} from "react-router-dom";
import {Card, CardBody, Row, Col} from "reactstrap";
import {Form, FormGroup, Input} from "reactstrap";
import SearchCollection from "../components/Search/SearchCollection";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import {useHelp} from "../contexts/HelpContext";
import PageHeader from "../components/Common/PageHeader";

import {notify} from "../utils/toast";
import ReactGA from "react-ga4";
import awsconfig from "../configs/awsconfig.json";
import {GoogleAnalyticsEventCategories} from "../constants";

const Search = () => {
  const [searchResultsData, setSearchResultsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const {query} = useParams<{query: string | undefined}>();
  const {openHelp} = useHelp();
  const navigate = useNavigate();

  const {control, handleSubmit, getValues, setValue} = useForm({
    defaultValues: {
      request: query || ""
    }
  });

  //console.log(watch("request")); // watch input value by passing the name of it

  // Notification function is now imported from utils/toast

  async function getData(q: string) {
    //console.log(`getData: query: ${q}`);
    setSearchResultsData([]);
    setLoading(true);
    setHasSearched(true);
    try {
      const {body} = await get({
        apiName: "data",
        path: `/search?q=${q}`
      }).response;
      const data = (await body.json()) as any;
      setSearchResultsData(data);
      setLoading(false);
    } catch {
      //console.error(`error: ${err}`);
      setLoading(false);
      return undefined;
    }
  }

  async function copyToClipBoard(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      notify("Link copied to clipboard");
    } catch {
      //console.log(err);
    }
  }

  async function getShareLink() {
    const link: string = `${awsconfig.Environment.hostname}/search/${encodeURI(getValues("request"))}`;

    ReactGA.event({
      category: GoogleAnalyticsEventCategories.REPORT,
      action: "get share link search",
      label: `${link}`
    });

    try {
      await copyToClipBoard(link);
    } catch {
      //console.log(`error getting share link: ${err}`);
      return undefined;
    }
  }

  useEffect(() => {
    async function initialLoad() {
      if (query && query !== "") {
        setValue("request", query); // Update form when navigating
        await getData(query);
      } else {
        // Clear results when navigating to /search without query
        setSearchResultsData([]);
        setHasSearched(false);
        setValue("request", "");
      }
    }
    initialLoad();
  }, [query, setValue]);

  async function onSubmit(formData: any) {
    // Update URL when searching via form
    if (formData.request) {
      navigate(`/search/${encodeURIComponent(formData.request)}`);
    }
    await getData(formData.request);
  }

  function handleExampleClick(searchTerm: string) {
    setValue("request", searchTerm);
    navigate(`/search/${encodeURIComponent(searchTerm)}`);
    getData(searchTerm);
  }

  function handleClearSearch() {
    setValue("request", "");
    setSearchResultsData([]);
    setHasSearched(false);
    navigate("/search");
  }

  return (
    <BackgroundColorContext.Consumer>
      {() => (
        <>
          <PageHeader
            title="Search Systems"
            subtitle="Find and compare computer performance data"
            icon="fa fa-search"
          />
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup className="mb-3">
                      <Controller
                        name="request"
                        control={control}
                        render={({field}) => (
                          <Input
                            placeholder="Search for CPU, GPU, Profile, World or Game Client Version"
                            type="text"
                            {...field}
                          />
                        )}
                      />
                    </FormGroup>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              {/* Search actions - always visible, centered */}
              <div className="text-center mb-3">
                {searchResultsData.length === 0 && hasSearched && (
                  <>
                    <a
                      href="#"
                      className="text-info"
                      onClick={(e) => {
                        e.preventDefault();
                        handleClearSearch();
                      }}
                    >
                      Show Examples
                    </a>
                  </>
                )}
                {searchResultsData.length > 0 && (
                  <>
                    <a
                      href="#"
                      className="text-info"
                      onClick={(e) => {
                        e.preventDefault();
                        handleClearSearch();
                      }}
                    >
                      New Search
                    </a>
                    <span className="mx-2 text-muted">&nbsp;|&nbsp;</span>
                    <a
                      href="#"
                      className="text-info"
                      onClick={(e) => {
                        e.preventDefault();
                        getShareLink();
                      }}
                    >
                      Share
                    </a>
                  </>
                )}
              </div>
            </Col>
          </Row>
          {searchResultsData.length === 0 && !loading && !hasSearched && (
            <Row>
              <Col md="12">
                <Card>
                  <CardBody>
                    <Row>
                      <Col md="12">
                        <div className="mt-3">
                          <h6 className="mb-3">
                            <i className="fa fa-lightbulb text-warning mr-2" />
                            Search Examples - Click to Try
                          </h6>
                          <div className="row">
                            <div className="col-md-4">
                              <p className="mb-2">
                                <strong>Basic Hardware Search:</strong>
                              </p>
                              <ul className="search-examples__list">
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("RTX 4060");
                                    }}
                                  >
                                    <code>RTX 4060</code>
                                  </a>{" "}
                                  - Popular gaming GPU
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("i5-13400");
                                    }}
                                  >
                                    <code>i5-13400</code>
                                  </a>{" "}
                                  - Popular Intel CPU
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("Ryzen 7 7700X");
                                    }}
                                  >
                                    <code>Ryzen 7 7700X</code>
                                  </a>{" "}
                                  - Popular AMD CPU
                                </li>
                              </ul>
                              <p className="mb-2">
                                <strong>Boolean Operators:</strong>
                              </p>
                              <ul className="search-examples__list">
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("RTX 4070 AND i9");
                                    }}
                                  >
                                    <code>RTX 4070 AND i9</code>
                                  </a>{" "}
                                  - High-end combo
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("M3 OR M4");
                                    }}
                                  >
                                    <code>M3 OR M4</code>
                                  </a>{" "}
                                  - Newer Apple silicon CPUs
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("RTX !3050");
                                    }}
                                  >
                                    <code>RTX !3050</code>
                                  </a>{" "}
                                  - RTX except 3050
                                </li>
                              </ul>
                            </div>
                            <div className="col-md-4">
                              <p className="mb-2">
                                <strong>Apple Performance:</strong>
                              </p>
                              <ul className="search-examples__list">
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("A15");
                                    }}
                                  >
                                    <code>A15</code>
                                  </a>{" "}
                                  - 2022 Apple TV
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick('"M4 Max" AND Makuri');
                                    }}
                                  >
                                    <code>"M4 Max" AND Makuri</code>
                                  </a>{" "}
                                  - Exact "M4 Max" match AND Makuri Island world
                                </li>
                              </ul>
                              <p className="mb-2">
                                <strong>Gaming Laptop Combos:</strong>
                              </p>
                              <ul className="search-examples__list">
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("i7 RTX 4060");
                                    }}
                                  >
                                    <code>i7 RTX 4060</code>
                                  </a>{" "}
                                  - Popular gaming setup
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("Ryzen RTX 3060");
                                    }}
                                  >
                                    <code>Ryzen RTX 3060</code>
                                  </a>{" "}
                                  - AMD + NVIDIA combo
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("GTX 1660 Ti");
                                    }}
                                  >
                                    <code>GTX 1660 Ti</code>
                                  </a>{" "}
                                  - Budget gaming GPU
                                </li>
                              </ul>
                            </div>
                            <div className="col-md-4">
                              <p className="mb-2">
                                <strong>Advanced Search Operators:</strong>
                              </p>
                              <ul className="search-examples__list">
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("Solo +GTX");
                                    }}
                                  >
                                    <code>Solo +GTX</code>
                                  </a>{" "}
                                  - Solo rider GTX performance
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("RTX* -3050");
                                    }}
                                  >
                                    <code>RTX* -3050</code>
                                  </a>{" "}
                                  - Any RTX except 3050
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("(M3 OR M4) AND Pro");
                                    }}
                                  >
                                    <code>(M3 OR M4) AND Pro</code>
                                  </a>{" "}
                                  - Latest Pro Macs
                                </li>
                              </ul>
                              <p className="mb-2">
                                <strong>Situation-Specific Search:</strong>
                              </p>
                              <ul className="search-examples__list">
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick("Fondo Makuri Ultra 4K PC");
                                    }}
                                  >
                                    <code>Fondo Makuri Ultra 4K PC</code>
                                  </a>{" "}
                                  - Massive group, complex world in 4K
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleExampleClick('High 1080 "New York" Company Mac');
                                    }}
                                  >
                                    <code>High 1080 "New York" Company Mac</code>
                                  </a>{" "}
                                  - Small group, high graphics, Mac
                                </li>
                              </ul>
                            </div>
                          </div>
                          <div className="text-center mt-3">
                            <small className="text-muted">
                              <a
                                href="#"
                                className="text-info"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openHelp("searchBasics");
                                }}
                              >
                                <i className="fa fa-question-circle mr-1" />
                              </a>
                              Need more help? Check the{" "}
                              <a
                                href="#"
                                className="text-info"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openHelp("searchBasics");
                                }}
                              >
                                <strong>Search Guide</strong>
                              </a>{" "}
                              in the Help system for advanced techniques.
                            </small>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <Row>
                    <Col md="12">
                      {loading && (
                        <div className="iconWrapper">
                          <RingLoader color="#4682b4" size={80} loading={true} />
                        </div>
                      )}
                      {!loading && <SearchCollection items={searchResultsData} showLinks={false} />}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default Search;
